import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Shield, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '../contexts/AuthContext';
import shomriLogo from '@/assets/shomri-logo.svg';

const LoginPage = () => {
  const {
    code,
    setCode,
    isAuthenticated,
    isLoading,
    hasError
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-5xl h-[700px] bg-background rounded-2xl shadow-xl overflow-hidden flex border border-border">
        {/* Left Side - Branding */}
        <div className="flex-1 bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col items-center justify-center p-10 relative">
          {/* Decorative gradient blobs */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-accent/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-10 w-24 h-24 bg-[hsl(var(--shomri-blue))]/10 rounded-full blur-2xl" />
          
          <div className="max-w-sm text-center relative z-10">
            {/* Shomri Logo */}
            <div className="mb-8">
              <img src={shomriLogo} alt="Shomri" className="h-10 mx-auto" />
            </div>

            <h1 className="text-2xl font-semibold text-foreground mb-3 tracking-tight">Filter Installer</h1>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Set up device protection in just a few simple steps. No technical knowledge required.
            </p>

            {/* Feature highlights */}
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-card/60 backdrop-blur-sm border border-border/50">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Quick Setup</p>
                  <p className="text-xs text-muted-foreground">Ready in under 5 minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-xl bg-card/60 backdrop-blur-sm border border-border/50">
                <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Safe & Secure</p>
                  <p className="text-xs text-muted-foreground">Peace of mind for the whole family</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 bg-card flex items-center justify-center p-12">
          <div className="w-full max-w-xs">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-2 tracking-tight">
                Welcome
              </h2>
              <p className="text-muted-foreground">
                Enter your device code to continue
              </p>
            </div>

            <form onSubmit={e => e.preventDefault()} noValidate>
              {/* Code Input */}
              <div className="flex justify-center mb-6">
                <InputOTP maxLength={6} value={code} onChange={value => setCode(value.toUpperCase())} pattern="[A-Za-z0-9]" inputMode="text" autoCapitalize="characters" autoCorrect="off" spellCheck={false}>
                  <InputOTPGroup className="gap-2">
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl font-semibold rounded-xl border-2 border-border focus:border-primary" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {/* Error State */}
              {hasError && code.length === 6 && (
                <div className="text-center p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-xl">
                  <p className="text-sm text-destructive font-medium">Device not found</p>
                  <p className="text-xs text-destructive/70 mt-1">Please check your code and try again</p>
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base rounded-xl transition-all duration-200 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5" 
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Continue'}
              </Button>
            </form>

            {/* Help Link */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Don't have a code?
              </p>
              <Button variant="outline" size="sm" className="rounded-xl border-border hover:bg-muted/50">
                <HelpCircle className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
